<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { vOnClickOutside } from '@vueuse/components';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import {
  LABEL_STATE,
  buildLabelChanges,
  getLabelStates,
  nextLabelState,
  sortLabelsByState,
} from './labelHelpers';

const props = defineProps({
  conversations: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['assign', 'close', 'update']);

const { t } = useI18n();

const labels = useMapGetter('labels/getLabels');

const query = ref('');
// Estados ao abrir o menu: a ordem da lista usa estes (não muda enquanto clica).
const initialStates = getLabelStates(props.conversations);
const labelStates = ref({ ...initialStates });

const stateOf = title => labelStates.value[title] || LABEL_STATE.NONE;
const ariaChecked = title => {
  const state = stateOf(title);
  if (state === LABEL_STATE.SOME) return 'mixed';
  return state === LABEL_STATE.ALL ? 'true' : 'false';
};

const orderedLabels = computed(() =>
  sortLabelsByState(labels.value, initialStates)
);

const filteredLabels = computed(() => {
  if (!query.value) return orderedLabels.value;
  return orderedLabels.value.filter(label =>
    label.title.toLowerCase().includes(query.value.toLowerCase())
  );
});

const hasLabels = computed(() => labels.value.length > 0);
const hasFilteredLabels = computed(() => filteredLabels.value.length > 0);
const labelChanges = computed(() =>
  buildLabelChanges(initialStates, labelStates.value)
);
const hasChanges = computed(() => {
  return labelChanges.value.add.length || labelChanges.value.remove.length;
});

const toggleLabel = title => {
  labelStates.value = {
    ...labelStates.value,
    [title]: nextLabelState(stateOf(title), initialStates[title]),
  };
};

const onClose = () => {
  emit('close');
};

const handleUpdate = () => {
  if (!hasChanges.value) return;

  emit('update', labelChanges.value);
  emit(
    'assign',
    Object.keys(labelStates.value).filter(
      title => stateOf(title) === LABEL_STATE.ALL
    )
  );
};
</script>

<template>
  <div
    v-on-click-outside="onClose"
    class="absolute ltr:right-2 rtl:left-2 top-12 origin-top-right z-20 w-60 bg-n-alpha-3 backdrop-blur-[100px] border-n-weak rounded-lg border border-solid shadow-md"
    role="dialog"
    aria-labelledby="label-dialog-title"
  >
    <div class="triangle">
      <svg height="12" viewBox="0 0 24 12" width="24">
        <path d="M20 12l-8-8-12 12" fill-rule="evenodd" stroke-width="1px" />
      </svg>
    </div>
    <div class="flex items-center justify-between p-2.5">
      <span class="text-sm font-medium">{{
        t('BULK_ACTION.LABELS.MANAGE_LABELS')
      }}</span>
      <NextButton ghost xs slate icon="i-lucide-x" @click="onClose" />
    </div>
    <div class="flex flex-col max-h-60 min-h-0">
      <header class="py-2 px-2.5">
        <Input
          v-model="query"
          type="search"
          :placeholder="t('BULK_ACTION.SEARCH_INPUT_PLACEHOLDER')"
          icon-left="i-lucide-search"
          size="sm"
          class="w-full"
          :aria-label="t('BULK_ACTION.SEARCH_INPUT_PLACEHOLDER')"
        />
      </header>
      <ul
        v-if="hasLabels"
        class="flex-1 overflow-y-auto m-0 list-none"
        role="listbox"
        :aria-label="t('BULK_ACTION.LABELS.MANAGE_LABELS')"
      >
        <li v-if="!hasFilteredLabels" class="p-2 text-center">
          <span class="text-sm text-n-slate-11">{{
            t('BULK_ACTION.LABELS.NO_LABELS_FOUND')
          }}</span>
        </li>
        <li
          v-for="label in filteredLabels"
          :key="label.id"
          class="my-1 mx-0 py-0 px-2.5"
          role="option"
          :aria-selected="stateOf(label.title) !== LABEL_STATE.NONE"
        >
          <div
            role="checkbox"
            tabindex="0"
            :aria-checked="ariaChecked(label.title)"
            :aria-label="label.title"
            :title="
              stateOf(label.title) === LABEL_STATE.SOME
                ? t('BULK_ACTION.LABELS.PARTIAL_HINT')
                : label.title
            "
            class="items-center rounded-md cursor-pointer flex py-1 px-2.5 hover:bg-n-slate-3 dark:hover:bg-n-solid-3"
            :class="{
              'bg-n-slate-2': stateOf(label.title) !== LABEL_STATE.NONE,
            }"
            @click="toggleLabel(label.title)"
            @keydown.space.prevent="toggleLabel(label.title)"
            @keydown.enter.prevent="toggleLabel(label.title)"
          >
            <span
              class="flex items-center justify-center flex-shrink-0 w-4 h-4 rounded border border-solid ltr:mr-2.5 rtl:ml-2.5"
              :class="
                stateOf(label.title) === LABEL_STATE.NONE
                  ? 'border-n-slate-8 bg-transparent'
                  : 'border-n-brand bg-n-brand text-white'
              "
            >
              <span
                v-if="stateOf(label.title) === LABEL_STATE.ALL"
                class="i-lucide-check w-3 h-3"
              />
              <span
                v-else-if="stateOf(label.title) === LABEL_STATE.SOME"
                class="i-lucide-minus w-3 h-3"
              />
            </span>
            <span
              class="overflow-hidden flex-grow w-full text-sm whitespace-nowrap text-ellipsis"
            >
              {{ label.title }}
            </span>
            <span
              class="rounded-md h-3 w-3 flex-shrink-0 border border-solid border-n-weak"
              :style="{ backgroundColor: label.color }"
            />
          </div>
        </li>
      </ul>
      <div v-else class="p-2 text-center">
        <span class="text-sm text-n-slate-11">{{
          t('CONTACTS_BULK_ACTIONS.NO_LABELS_FOUND')
        }}</span>
      </div>
      <footer class="p-2">
        <NextButton
          sm
          type="submit"
          class="w-full"
          :label="t('BULK_ACTION.LABELS.UPDATE_SELECTED_LABELS')"
          :disabled="!hasChanges"
          @click="handleUpdate"
        />
      </footer>
    </div>
  </div>
</template>

<style scoped lang="scss">
.triangle {
  @apply block z-10 absolute text-left -top-3 ltr:right-[--triangle-position] rtl:left-[--triangle-position];

  svg path {
    @apply fill-n-alpha-3 backdrop-blur-[100px]  stroke-n-weak;
  }
}
</style>
